// src/utils/criticalCss.js

/**
 * Critical CSS utility - Enhanced version
 * 
 * This module extracts and injects critical (above-the-fold) CSS
 * to improve initial page render performance with route-based optimization
 */

// Pre-defined critical CSS for key routes to avoid runtime computation
const routeCriticalCss = {
  home: `
    body { font-family: 'Inter', sans-serif; margin: 0; }
    .navbar { position: fixed; width: 100%; z-index: 1000; background-color: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
    .hero-section { position: relative; width: 100%; min-height: 70vh; display: flex; align-items: center; overflow: hidden; }
    .hero-content { position: relative; z-index: 2; padding: 2rem; max-width: 600px; margin: 0 auto; color: #fff; text-align: center; }
    .hero-content h1 { font-size: 2.5rem; margin-bottom: 1rem; font-weight: 700; }
    .hero-flag-bg { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background-size: cover; background-position: center; z-index: 1; }
    .featured-products-section { padding: 3rem 1rem; }
    .product-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 1.5rem; max-width: 1200px; margin: 0 auto; }
    .loading-spinner { border: 4px solid rgba(0, 0, 0, 0.1); border-left-color: #09f; border-radius: 50%; width: 24px; height: 24px; animation: spin 1s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }
  `,
  products: `
    body { font-family: 'Inter', sans-serif; margin: 0; }
    .navbar { position: fixed; width: 100%; z-index: 1000; background-color: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
    .product-list-page { padding-top: 80px; padding-bottom: 2rem; }
    .product-header { padding: 1rem; text-align: center; margin-bottom: 2rem; }
    .products-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 1.5rem; max-width: 1200px; margin: 0 auto; padding: 0 1rem; }
    .product-card { border: 1px solid #eee; border-radius: 8px; overflow: hidden; transition: transform 0.2s, box-shadow 0.2s; background-color: #fff; }
    .loading-spinner { border: 4px solid rgba(0, 0, 0, 0.1); border-left-color: #09f; border-radius: 50%; width: 24px; height: 24px; animation: spin 1s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }
    .filters-container { display: flex; flex-wrap: wrap; justify-content: center; gap: 1rem; margin-bottom: 1.5rem; }
  `,
  productDetail: `
    body { font-family: 'Inter', sans-serif; margin: 0; }
    .navbar { position: fixed; width: 100%; z-index: 1000; background-color: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
    .product-detail-page { padding-top: 80px; padding-bottom: 2rem; }
    .product-detail-container { display: flex; flex-direction: column; max-width: 1200px; margin: 0 auto; padding: 1rem; }
    @media (min-width: 768px) { .product-detail-container { flex-direction: row; gap: 2rem; } }
    .product-image-container { flex: 1; }
    .product-info-container { flex: 1; padding: 1rem; }
    .loading-spinner { border: 4px solid rgba(0, 0, 0, 0.1); border-left-color: #09f; border-radius: 50%; width: 24px; height: 24px; animation: spin 1s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }
  `,
};

/**
 * Get critical CSS based on current route
 * @param {string} pathname - Current pathname
 * @returns {string} Critical CSS for this route
 */
export function getRouteCriticalCss(pathname = window.location.pathname) {
  // Determine which route we're on
  if (pathname.includes('/products/') && !pathname.endsWith('/products')) {
    return routeCriticalCss.productDetail || '';
  } else if (pathname.includes('/products')) {
    return routeCriticalCss.products || '';
  } else if (pathname.includes('/home') || pathname === '/' || pathname.endsWith('/country/canada')) {
    return routeCriticalCss.home || '';
  }

  // Default CSS for other routes
  return `
    body { font-family: 'Inter', sans-serif; margin: 0; }
    .navbar { position: fixed; width: 100%; z-index: 1000; background-color: #fff; }
    .main-content { padding-top: 60px; }
    .loading-spinner { border: 4px solid rgba(0, 0, 0, 0.1); border-left-color: #09f; border-radius: 50%; width: 24px; height: 24px; animation: spin 1s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }
  `;
}

/**
 * Extract critical CSS rules needed for above-the-fold content
 * @param {string} selector - CSS selector to identify above-the-fold content
 * @returns {string} Critical CSS rules
 */
export function extractCriticalCss(selector = '#root') {
  try {
    // Get the container element
    const container = document.querySelector(selector);
    if (!container) return '';

    // Get all stylesheet rules
    const styleSheets = Array.from(document.styleSheets);
    let criticalRules = [];

    // Process each stylesheet
    styleSheets.forEach(sheet => {
      try {
        // Skip external stylesheets from different domains (CORS restriction)
        if (sheet.href && new URL(sheet.href).origin !== window.location.origin) {
          return;
        }

        const rules = Array.from(sheet.cssRules || []);
        
        rules.forEach(rule => {
          // Handle different rule types
          if (rule instanceof CSSStyleRule) {
            // Check if this rule applies to any visible element
            const selector = rule.selectorText;
            try {
              const matchedElements = container.querySelectorAll(selector);
              
              // Check if any matching element is in the viewport
              const isVisible = Array.from(matchedElements).some(el => {
                const rect = el.getBoundingClientRect();
                return (
                  rect.top < window.innerHeight &&
                  rect.left < window.innerWidth &&
                  rect.bottom > 0 &&
                  rect.right > 0
                );
              });
              
              if (isVisible) {
                criticalRules.push(rule.cssText);
              }
            } catch (e) {
              // Some selectors might cause errors, skip them
            }
          } else if (rule instanceof CSSMediaRule && window.matchMedia(rule.conditionText).matches) {
            // Include media queries that currently apply
            criticalRules.push(rule.cssText);
          }
        });
      } catch (e) {
        // Skip stylesheets that can't be read due to CORS
      }
    });

    return criticalRules.join('\n');
  } catch (error) {
    console.error('Error extracting critical CSS:', error);
    return '';
  }
}

/**
 * Inject critical CSS into the document head
 * @param {string} css - CSS rules to inject
 * @param {boolean} isPrioritized - Whether to prioritize this CSS
 */
export function injectCriticalCss(css, isPrioritized = true) {
  if (!css || typeof document === 'undefined') return;
  
  // Remove any existing critical CSS if replacing
  if (isPrioritized) {
    const existingStyles = document.querySelectorAll('style[data-critical="true"]');
    existingStyles.forEach(el => el.parentNode.removeChild(el));
  }
  
  const style = document.createElement('style');
  style.setAttribute('data-critical', 'true');
  if (isPrioritized) {
    style.setAttribute('data-prioritized', 'true');
  }
  style.textContent = css;
  
  // Insert at the beginning of <head> for highest priority
  if (document.head.firstChild) {
    document.head.insertBefore(style, document.head.firstChild);
  } else {
    document.head.appendChild(style);
  }
}

/**
 * Preload key assets (fonts, hero images) for faster rendering
 * @param {Array} assets - Assets to preload
 */
export function preloadCriticalAssets(assets = []) {
  if (typeof document === 'undefined') return;
  
  // Default critical assets if none provided
  if (!assets.length) {
    assets = [
      // Preload hero image for home page (font already preloaded in HTML template)
      { href: '/static/media/Hero_canada.png', as: 'image' }
    ];
  }
  
  assets.forEach(asset => {
    // Check if this preload link already exists to avoid duplicates
    const existingLink = document.querySelector(`link[rel="preload"][href="${asset.href}"]`);
    if (existingLink) {
      return; // Skip if already exists
    }
    
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = asset.href;
    link.as = asset.as;
    
    if (asset.type) {
      link.type = asset.type;
    }
    
    if (asset.crossorigin) {
      link.crossOrigin = asset.crossorigin;
    }
    
    document.head.appendChild(link);
  });
}

/**
 * Load non-critical CSS asynchronously
 * @param {string} href - URL to the CSS file
 */
export function loadCssAsync(href) {
  if (typeof document === 'undefined') return;
  
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.media = 'print';
  document.head.appendChild(link);
  
  // Once loaded, switch to 'all' media to apply styles
  link.onload = () => {
    link.media = 'all';
  };
}

/**
 * Initialize client-side optimization for current route
 */
export function initClientOptimizations() {
  if (typeof window === 'undefined') return;

  // Inject route-specific critical CSS immediately
  const criticalCss = getRouteCriticalCss();
  injectCriticalCss(criticalCss);
  
  // Preload critical assets
  preloadCriticalAssets();
  
  // Defer non-critical resource loading
  window.addEventListener('load', () => {
    // Load non-critical CSS after page load
    if (window.DEFERRED_STYLES) {
      window.DEFERRED_STYLES.forEach(href => loadCssAsync(href));
    }
    
    // Prefetch other routes for faster subsequent navigation
    setTimeout(() => {
      const routesToPrefetch = ['/country/canada/products', '/country/canada/about'];
      routesToPrefetch.forEach(route => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = route;
        document.head.appendChild(link);
      });
    }, 3000); // Wait 3 seconds after load to prefetch
  });
}